export interface WalkthroughStep {
  id: number
  title: string
  description: string
  tip: string
  mockupType: string
}

export interface MortgagePainPoint {
  title: string
  description: string
  icon: 'papers' | 'clock' | 'shield' | 'notes'
}

export interface MortgageFeature {
  title: string
  description: string
  icon: 'mic' | 'ramble' | 'document' | 'workflow'
  walkthroughSteps: WalkthroughStep[]
}

export interface MortgageDocumentTemplate {
  title: string
}

export interface MortgageStep {
  number: number
  title: string
  description: string
}

export const painPoints: MortgagePainPoint[] = [
  {
    title: 'Hours lost to document preparation',
    description:
      'Suitability letters, KFIs, and recommendation reports take hours to draft — pulling you away from the clients who need your advice.',
    icon: 'papers',
  },
  {
    title: 'Compliance paperwork never ends',
    description:
      'Every case demands meticulous file notes, audit trails, and regulatory documentation that eats into your productive hours.',
    icon: 'clock',
  },
  {
    title: 'Client pipeline is hard to track',
    description:
      'Applications, valuations, and completions across multiple lenders — keeping on top of every case feels impossible without the right tools.',
    icon: 'shield',
  },
  {
    title: 'Meeting notes scattered everywhere',
    description:
      'Client consultations, fact-finds, and follow-up calls happen constantly — but the notes end up in emails, notebooks, and shared drives with no structure.',
    icon: 'notes',
  },
]

export const features: MortgageFeature[] = [
  {
    title: 'Meeting Capture & Transcription',
    description:
      'Record client consultations, fact-finds, and review meetings. AI produces structured notes automatically — no more frantic scribbling.',
    icon: 'mic',
    walkthroughSteps: [
      {
        id: 1,
        title: 'Start your meeting recording',
        description:
          'Hit record as your client consultation begins. WealthSpace captures audio directly from your device — whether in person, on a video call, or over the phone.',
        tip: 'Works with Zoom, Teams, and in-person meetings. No special hardware needed.',
        mockupType: 'mic-setup',
      },
      {
        id: 2,
        title: 'AI listens and transcribes in real-time',
        description:
          'While you focus on your client, WealthSpace transcribes the conversation and identifies key topics like property details, income, and lending requirements.',
        tip: 'The AI recognises mortgage-specific terminology and structures notes around your advice process.',
        mockupType: 'recording',
      },
      {
        id: 3,
        title: 'Review structured meeting notes',
        description:
          'After the meeting, you get organised notes broken down by topic — client objectives, property details, affordability, and next steps. All searchable and editable.',
        tip: 'Edit any section before saving. Notes are automatically linked to the client file.',
        mockupType: 'notes-ready',
      },
      {
        id: 4,
        title: 'Generate documents from your notes',
        description:
          'Turn meeting notes into suitability letters, compliance file notes, or client summaries with one click. The AI uses your meeting context to produce accurate, detailed documents.',
        tip: 'The more you use WealthSpace, the better it learns your writing style and preferences.',
        mockupType: 'document-export',
      },
    ],
  },
  {
    title: 'Ramble-to-Document',
    description:
      'Dictate your advice rationale and case notes. AI transforms rambles into structured suitability letters, recommendation reports, or client communications.',
    icon: 'ramble',
    walkthroughSteps: [
      {
        id: 1,
        title: 'Open ramble mode',
        description:
          'Tap the ramble button and start speaking. Describe your advice rationale, case notes, or anything you would normally type up later.',
        tip: 'You can ramble while driving between appointments — dictate case notes hands-free.',
        mockupType: 'ramble-start',
      },
      {
        id: 2,
        title: 'Speak freely and naturally',
        description:
          'Talk through the mortgage case as you would explain it to a colleague. Mention the client, the property, the lender, and your reasoning. No structure needed.',
        tip: "Don't worry about grammar or order — the AI handles all of that for you.",
        mockupType: 'recording',
      },
      {
        id: 3,
        title: 'AI transforms your words',
        description:
          'WealthSpace analyses your dictation and restructures it into a properly formatted document — choosing the right template automatically based on your content.',
        tip: "You can specify the output type: 'Make this a suitability letter' or 'Create a file note'.",
        mockupType: 'processing',
      },
      {
        id: 4,
        title: 'Review and send',
        description:
          'Edit the polished draft, make any adjustments, and export as PDF or send directly to clients and lenders. Every document maintains a full audit trail.',
        tip: 'Every document keeps an audit trail back to the original recording for compliance.',
        mockupType: 'document-export',
      },
    ],
  },
  {
    title: 'Document Generation',
    description:
      'AI-powered templates tailored to mortgage advice workflows. Generate suitability letters, compliance file notes, and client reports in minutes.',
    icon: 'document',
    walkthroughSteps: [
      {
        id: 1,
        title: 'Choose your template',
        description:
          'Browse mortgage-specific templates: suitability letters, recommendation reports, compliance file notes, KFI documents, protection recommendations, and more.',
        tip: 'Favourite your most-used templates for quick access from the dashboard.',
        mockupType: 'template-grid',
      },
      {
        id: 2,
        title: 'Select client and context',
        description:
          'Pick the client from your list. WealthSpace pulls in their details, case history, and recent meeting notes to pre-fill the document with accurate context.',
        tip: 'Client data is auto-populated — no copying and pasting from other systems.',
        mockupType: 'client-select',
      },
      {
        id: 3,
        title: 'AI generates your draft',
        description:
          'WealthSpace combines the template, client context, and your meeting notes to produce a comprehensive first draft. Tailored to mortgage advice standards.',
        tip: 'Generation typically takes under 30 seconds, even for complex suitability reports.',
        mockupType: 'processing',
      },
      {
        id: 4,
        title: 'Review, customise, and export',
        description:
          'Fine-tune the AI draft with the built-in editor. Adjust wording, add case-specific details, then export as PDF or share directly with clients and lenders.',
        tip: 'All generated documents are stored in the client file with version history.',
        mockupType: 'editor',
      },
    ],
  },
  {
    title: 'Practice Management',
    description:
      'Track cases from consultation to completion. Manage your pipeline, review cycles, and compliance deadlines in one place.',
    icon: 'workflow',
    walkthroughSteps: [
      {
        id: 1,
        title: 'View your pipeline',
        description:
          'See all your active cases at a glance — from initial consultation through to completion. Know exactly where every case stands across every lender.',
        tip: 'Filter by status, lender, or date to focus on what matters right now.',
        mockupType: 'pipeline',
      },
      {
        id: 2,
        title: 'Track deadlines and tasks',
        description:
          'Never miss a mortgage offer expiry, valuation deadline, or follow-up call. WealthSpace tracks every key date across your entire caseload automatically.',
        tip: 'Set up email or notification reminders for approaching deadlines.',
        mockupType: 'deadlines',
      },
      {
        id: 3,
        title: 'Manage compliance workflows',
        description:
          "Built-in checklists ensure every case meets regulatory requirements. Track what's done and what's outstanding — from fact-find to client sign-off.",
        tip: "Compliance checklists are customisable to match your firm's processes.",
        mockupType: 'compliance',
      },
      {
        id: 4,
        title: 'Review performance',
        description:
          'Track conversion rates, average case times, and revenue. Understand where your practice is thriving and where to improve with clear, actionable data.',
        tip: 'Export reports for management reviews or regulatory audits.',
        mockupType: 'analytics',
      },
    ],
  },
]

export const documentTemplates: MortgageDocumentTemplate[] = [
  { title: 'Suitability Letters' },
  { title: 'KFI / ESIS Documents' },
  { title: 'Client Fact-Finds' },
  { title: 'Mortgage Recommendation Reports' },
  { title: 'Protection Recommendations' },
  { title: 'Compliance File Notes' },
  { title: 'Client Review Notes' },
  { title: 'Remortgage Assessments' },
  { title: 'Product Transfer Analysis' },
  { title: 'Affordability Assessments' },
  { title: 'BTL Assessment Reports' },
  { title: 'Annual Review Documents' },
]

export const steps: MortgageStep[] = [
  {
    number: 1,
    title: 'Record or ramble your advice',
    description:
      'Use meeting capture for client consultations or simply dictate your advice rationale, case notes, and recommendations.',
  },
  {
    number: 2,
    title: 'AI structures and drafts your documents',
    description:
      'WealthSpace transforms your input into polished suitability letters, recommendation reports, compliance notes, and client communications.',
  },
  {
    number: 3,
    title: 'Review, tailor, and approve',
    description:
      'Edit AI-generated drafts with full control. Refine language, add specifics, and ensure everything meets your standards.',
  },
  {
    number: 4,
    title: 'Send to clients and complete the file',
    description:
      'Share finalised documents with clients and lenders. Maintain a complete audit trail of every communication and decision.',
  },
]
